import { ChevronDown, Check } from "lucide-react";

/**
 * StageAccordion — tarjeta plegable de una etapa del flujo cronológico.
 *
 * Props:
 *  - stepNumber: 1..5
 *  - label: nombre visible
 *  - description?: subtítulo opcional
 *  - state: 'completed' | 'current' | 'future'
 *  - isOpen: boolean
 *  - onToggle: () => void
 *  - count?: string a la derecha (ej "3 archivos")
 *  - testid: base para data-testid
 *  - children: contenido expandido
 */
export default function StageAccordion({
  stepNumber, label, description, state = "current",
  isOpen, onToggle, count, testid, children,
}) {
  const stateStyles = {
    current: "border-blue-400 shadow-sm bg-white",
    completed: "border-emerald-200 bg-white",
    future: "border-slate-200 bg-slate-50",
  };
  const numStyles = {
    current: "bg-blue-600 text-white",
    completed: "bg-emerald-500 text-white",
    future: "bg-slate-200 text-slate-500",
  };
  const labelStyles = {
    current: "text-slate-900",
    completed: "text-slate-700",
    future: "text-slate-500",
  };

  return (
    <section
      className={`rounded-2xl border-2 transition-all overflow-hidden ${stateStyles[state]}`}
      data-testid={`${testid}-card`}
      data-stage-state={state}
      data-stage-open={isOpen ? "1" : "0"}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full flex items-center gap-3 px-4 py-4 sm:px-5 sm:py-4 text-left hover:bg-slate-50/60 transition-colors min-h-[64px]"
        data-testid={`${testid}-toggle`}
      >
        {/* Step badge / check */}
        <div
          className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center font-heading font-extrabold text-sm ${numStyles[state]}`}
          data-testid={`${testid}-badge`}
        >
          {state === "completed" ? <Check size={18} strokeWidth={3} /> : stepNumber}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className={`font-heading font-extrabold text-base sm:text-lg leading-tight ${labelStyles[state]}`}>
              {label}
            </h2>
            {state === "current" && (
              <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
                Actual
              </span>
            )}
            {count && (
              <span className="text-xs text-slate-500 font-mono">· {count}</span>
            )}
          </div>
          {description && (
            <div className={`text-xs mt-0.5 ${state === "future" ? "text-slate-400" : "text-slate-500"}`}>
              {description}
            </div>
          )}
        </div>

        <ChevronDown
          size={22}
          className={`flex-shrink-0 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          data-testid={`${testid}-chevron`}
        />
      </button>

      {/* Body: mount solo cuando isOpen para performance en móvil (evita render de fotos ocultas) */}
      {isOpen && (
        <div className="px-4 pb-5 sm:px-5 border-t border-slate-100" data-testid={`${testid}-body`}>
          <div className="pt-4">{children}</div>
        </div>
      )}
    </section>
  );
}
